import { useContext } from 'react'

import {
  Box, Button, Checkbox, ListItem, Form, ModalContext, SidebarModal, Text,
} from '@domain-group/suit-theme'

import { deepSetObject, digObject, toggleArray } from '@domain-group/javascript-utils'

import useStatus, { useStatusForm } from '@hooks/useStatus'
import useServices from '@hooks/useServices'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { ServiceModel } from '@models/types'

const MODAL_KEY = 'EditStatusModal'

type HandleToggleParams = {
  options: object,
  selectedServices: Array<String>,
  setEntityState: Function,
  service: ServiceModel,
}

const handleToggle = (params: HandleToggleParams) => {
  const {
    options, selectedServices, setEntityState, service,
  } = params
  const serviceLowerCase = service.title.toLowerCase()
  const updatedServices = toggleArray(selectedServices, serviceLowerCase)

  // @ts-expect-error TS2345
  const updatedOptions = deepSetObject(options, 'services', updatedServices)

  setEntityState({ options: updatedOptions })
}

type ModalCallbacks = {
  closeModal: () => void,
  updateStatus: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const EditStatusModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateStatus } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { status } = modalPayload

  const {
    callbacks: { updateStatus: updateFn },
    updating,
  } = useStatus(status)

  const {
    entityState,
    entityState: {
      options,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useStatusForm(status, { validateOn: 'change' })

  const { confirm_toggle } = options || {}
  const selectedServices = digObject(options, 'services', [])

  const servicesPayload = useServices()
  const { filteredServices } = servicesPayload

  const updateStatusPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Status Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Edit" titleSecondLine="Status" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="Confirm Toggle" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select name="options.confirm_toggle" data-value-type="boolean" value={confirm_toggle} {...handlers}>
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>
        </Form>

        <Box flexDirection="column" marginTop="large" marginBottom="large">
          <Text color="mysteryGrey" fontSize="xsmall">Service Options</Text>
        </Box>

        {filteredServices.map(service => (
          // @ts-expect-error TS2339
          <ListItem.WithCheckbox
            boxProps={{ border: 'none' }}
            checkbox={(
              <Checkbox
                checked={selectedServices.includes(service.title.toLowerCase())}
                onClick={() => handleToggle({
                  options, selectedServices, setEntityState, service,
                })}
              />
            )}
            disableHover
            key={service.id}
          >
            <Box color="bodyFontColor" fontSize="small">
              {service.title}
            </Box>
            {/*
           // @ts-expect-error TS2339 */}
          </ListItem.WithCheckbox>
        ))}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          // @ts-expect-error TS2345
          onClick={() => updateStatus(updateStatusPayload)}
          size="large"
        >
          {saveEnabled ? 'Update Status' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <EditStatusModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
